import { type AgendaItemDeleteText } from '@meetings/details/agenda/types';

export const AGENDA_CATEGORIES: {
    APPROVAL: number;
    INFORMATION: number;
    DISCUSSION: number;
    WORKSHOP: number;
    ORGANISATIONAL: number;
    APPROVAL_WITH_VOTING: number;
} = {
    APPROVAL: 1,
    INFORMATION: 2,
    DISCUSSION: 3,
    WORKSHOP: 4,
    ORGANISATIONAL: 5,
    APPROVAL_WITH_VOTING: 6
};

export const PRESENTERS_MAX_LENGTH = 200;
export const AGENDA_TITLE_MAX_LENGTH = 200;
export const AGENDA_NUMBER_MAX_LENGTH = 15;

export enum VotingOptionsCount {
    PENDING,
    NO,
    YES,
    ABSTAIN,
    REQUEST_DISCUSSION
}

export enum VoteDecision {
    LACK,
    NO,
    YES,
    ABSTAIN,
    REQUEST_DISCUSSION
}

export enum AgendaItemContentState {
    unlockedNoVotes,
    unlockedVotesExist,
    lockedDecided
}

export enum AgendaItemTypes {
    APPROVAL = 'approval'
}

export enum WhiteListTypes {
    CALENDLY = 'CALENDLY',
    DRAWIZE = 'DRAWIZE',
    FIGMA = 'FIGMA',
    GOOGLE_DOC = 'GOOGLE_DOC',
    LOOM = 'LOOM',
    MENTIMETER = 'MENTIMETER',
    MENTIMETER_SHORTEN_URL = 'MENTIMETER_SHORTEN_URL',
    MIRO = 'MIRO',
    OFFICE = 'OFFICE',
    ONE_DRIVE = 'ONE_DRIVE',
    POWERBI = 'POWERBI',
    SHAREPOINT = 'SHAREPOINT',
    SLIDO = 'SLIDO',
    SLIDO_SHORTEN_URL = 'SLIDO_SHORTEN_URL',
    SPOTIFY = 'SPOTIFY',
    VIMEO = 'VIMEO',
    YOUTUBE = 'YOUTUBE',
    YOUTUBE_SHORTEN_URL = 'YOUTUBE_SHORTEN_URL'
}

export const WHITE_LIST_DOMAIN = {
    [WhiteListTypes.YOUTUBE]: 'youtube.com',
    [WhiteListTypes.YOUTUBE_SHORTEN_URL]: 'youtu.be',
    [WhiteListTypes.LOOM]: 'loom.com',
    [WhiteListTypes.FIGMA]: 'figma.com',
    [WhiteListTypes.MENTIMETER]: 'mentimeter.com',
    [WhiteListTypes.MENTIMETER_SHORTEN_URL]: 'menti.com',
    [WhiteListTypes.OFFICE]: 'office.com',
    [WhiteListTypes.ONE_DRIVE]: 'onedrive.live.com',
    [WhiteListTypes.SHAREPOINT]: 'sharepoint.com',
    [WhiteListTypes.SPOTIFY]: 'spotify.com',
    [WhiteListTypes.MIRO]: 'miro.com',
    [WhiteListTypes.GOOGLE_DOC]: 'google.com',
    [WhiteListTypes.SLIDO]: 'sli.com',
    [WhiteListTypes.SLIDO_SHORTEN_URL]: 'sli.do',
    [WhiteListTypes.DRAWIZE]: 'drawize.com',
    [WhiteListTypes.CALENDLY]: 'calendly.com',
    [WhiteListTypes.VIMEO]: 'vimeo.com',
    [WhiteListTypes.POWERBI]: 'powerbi.com'
};

export const getDeleteConfirmItemsText = (t: (str: string) => string): AgendaItemDeleteText => ({
    title: t('Delete agenda items'),
    content: t('The selected agenda item(s) including all comments will be permanently deleted.')
});

export const getDeleteConfirmItemsWithDocumentsText = (
    t: (str: string) => string
): AgendaItemDeleteText => ({
    title: t('Delete agenda items with documents'),
    content: t(
        'The selected agenda item(s) including all comments will be permanently deleted.\nDocuments are moved to document panel and can be allocated to another agenda item.\nOnce allocated, the previously made comments and annotations to the document will be visible again.'
    )
});

export const getDeleteConfirmDocumentsText = (
    t: (str: string, interpolations?: { [key: string]: number }) => string,
    count: number
): AgendaItemDeleteText => ({
    title:
        count === 1
            ? t('Delete document')
            : t('Delete {{ numberOfDocuments }} documents', { numberOfDocuments: count }),
    content: t('Deleting this document will have the following consequences.'),
    options: [t('All annotations will be lost'), t('All comments will be removed')]
});

export const getDeleteConfirmSingleDocumentText = (
    t: (str: string, interpolations?: { [key: string]: string }) => string,
    documentName = ''
): AgendaItemDeleteText => ({
    title: t('Delete "{{name}}"', { name: documentName }),
    content: t('Deleting this document will have the following consequences.'),
    options: [t('All annotations will be lost'), t('All comments will be removed')]
});

export const getDeleteConfirmLinkContentText = (
    t: (str: string, interpolations?: { [key: string]: string }) => string
): AgendaItemDeleteText => ({
    title: t('Delete link'),
    content: t('Deleting this link will have the following consequences.'),
    options: [t('All comments will be removed')]
});

export const getDeleteConfirmLinkContentItemsText = (
    t: (str: string, interpolations?: { [key: string]: number }) => string
): AgendaItemDeleteText => ({
    title: t('Delete links'),
    content: t('Deleting these links will have the following consequences.'),
    options: [t('All comments will be removed')]
});

export const getDeleteConfirmItemsRequestTextWithUsers = (
    t: (str: string) => string
): AgendaItemDeleteText => ({
    title: t('Delete agenda items'),
    content: t(
        'The selected agenda item(s) including all comments will be permanently deleted.\n\nAny people added to the meeting as part of the agenda item(s) only will be automatically removed from the meeting.\n\nAgenda items scheduled from a topic in Topic Hub can be scheduled again after being deleted.'
    )
});

export const getDeleteConfirmSingleItemRequestTextWithUsers = (
    t: (str: string) => string
): AgendaItemDeleteText => ({
    title: t('Delete requested agenda item'),
    content: t(
        'The selected agenda item including all comments will be permanently deleted.\n\nAny people added to the meeting as part of this agenda item only will be automatically removed from the meeting.\n\nThis agenda item was scheduled from a topic. You will be able to schedule the topic again from the Topic Hub after deleting this agenda item.'
    )
});

export enum CreatedVia {
    CONTRIBUTOR = 'contributor',
    COPY = 'copy',
    COPY_ROOM = 'copy_room',
    EMAIL_ATTACHMENT = 'email_attachment',
    REQUEST = 'request',
    SCHEDULED_REQUEST = 'scheduled_request',
    WEB_CLIENT = 'web_client'
}

export const MAXIMUM_BULK_UPLOAD_DOCS_SIDEPANEL_WIDTH = 65;
export const MINIMUM_BULK_UPLOAD_DOCS_SIDEPANEL_WIDTH = 35;
export const MAXIMUM_BULK_UPLOAD_DOCS_SIDEPANEL_WIDTH_SWITCH = 45;

export const CURRENT_BULK_UPLOAD_DOCS_RESIZE_VALUE = 'CURRENT_BULK_UPLOAD_DOCS_RESIZE_VALUE';

export const STATE_VALUE_DIVIDER = ':';

export const OUTDATED_AGENDA_ERROR_CODE = 409;
